import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  FormBuilderState,
  FormSection,
  FormField,
  FieldType,
} from '../types';
import { createTaskForm } from '../api/createTaskFormApiService';
import { TASK_FORM_LABELS } from '../constants/labels';

// ─── Validation ───────────────────────────────────────────────────────────────

interface OptionErrors {
  label?: string;
}

interface FieldErrors {
  label?: string;
  type?: string;
  minOptions?: string;
  options?: Record<string, OptionErrors>;
  rangeMin?: string;
  rangeMax?: string;
  rangeUnit?: string;
  attachmentFileTypes?: string;
  attachmentMaxSize?: string;
  attachmentMaxFiles?: string;
}

interface SectionErrors {
  name?: string;
  description?: string;
  minFields?: string;
  fields?: Record<string, FieldErrors>;
}

interface FormErrors {
  name?: string;
  description?: string;
  sections?: Record<string, SectionErrors>;
  general?: string;
}

export function validateForm(state: FormBuilderState): FormErrors {
  const errors: FormErrors = {};

  if (!state.name.trim()) {
    errors.name = TASK_FORM_LABELS.ERROR_FORM_NAME_REQUIRED;
  } else if (state.name.length > 200) {
    errors.name = TASK_FORM_LABELS.ERROR_FORM_NAME_MAX;
  }

  if (!state.description.trim()) {
    errors.description = TASK_FORM_LABELS.ERROR_FORM_DESCRIPTION_REQUIRED;
  }

  if (state.sections.length === 0) {
    errors.general = TASK_FORM_LABELS.ERROR_NO_SECTIONS;
  }

  const sectionErrors: Record<string, SectionErrors> = {};

  for (const section of state.sections) {
    const secErr: SectionErrors = {};

    if (!section.name.trim()) {
      secErr.name = TASK_FORM_LABELS.ERROR_SECTION_NAME_REQUIRED;
    }

    const fieldErrors: Record<string, FieldErrors> = {};
    for (const field of section.fields) {
      const fErr: FieldErrors = {};
      if (!field.label.trim()) {
        fErr.label = TASK_FORM_LABELS.ERROR_FIELD_LABEL_REQUIRED;
      }
      if (
        field.type === 'number_range' &&
        field.min != null &&
        field.max != null &&
        field.max <= field.min
      ) {
        fErr.rangeMax = TASK_FORM_LABELS.ERROR_RANGE_MAX_GT_MIN;
      }
      if (Object.keys(fErr).length > 0) {
        fieldErrors[field.id] = fErr;
      }
    }

    if (Object.keys(fieldErrors).length > 0) {
      secErr.fields = fieldErrors;
    }

    if (Object.keys(secErr).length > 0) {
      sectionErrors[section.id] = secErr;
    }
  }

  if (Object.keys(sectionErrors).length > 0) {
    errors.sections = sectionErrors;
  }

  return errors;
}

export function validateForPreview(state: FormBuilderState): FormErrors {
  const errors: FormErrors = {};

  if (!state.name.trim()) {
    errors.name = TASK_FORM_LABELS.ERROR_FORM_NAME_REQUIRED;
  }

  if (!state.description.trim()) {
    errors.description = TASK_FORM_LABELS.ERROR_FORM_DESCRIPTION_REQUIRED;
  }

  if (state.sections.length === 0) {
    errors.general = TASK_FORM_LABELS.ERROR_NO_SECTIONS;
    return errors;
  }

  const sectionErrors: Record<string, SectionErrors> = {};

  for (const section of state.sections) {
    const secErr: SectionErrors = {};

    if (!section.name.trim()) {
      secErr.name = TASK_FORM_LABELS.ERROR_SECTION_NAME_REQUIRED;
    }

    if (section.fields.length === 0) {
      secErr.minFields = TASK_FORM_LABELS.ERROR_SECTION_MIN_FIELDS;
    }

    const fieldErrors: Record<string, FieldErrors> = {};
    for (const field of section.fields) {
      const fErr: FieldErrors = {};

      if (!field.label.trim()) {
        fErr.label = TASK_FORM_LABELS.ERROR_FIELD_LABEL_REQUIRED;
      }

      if (!field.type) {
        fErr.type = TASK_FORM_LABELS.ERROR_FIELD_TYPE_REQUIRED;
      }

      if (field.type === 'number_range') {
        if (field.min === null || field.min === undefined) {
          fErr.rangeMin = TASK_FORM_LABELS.ERROR_FIELD_TYPE_REQUIRED;
        }
        if (field.max === null || field.max === undefined) {
          fErr.rangeMax = TASK_FORM_LABELS.ERROR_FIELD_TYPE_REQUIRED;
        } else if (field.min != null && field.max <= field.min) {
          fErr.rangeMax = TASK_FORM_LABELS.ERROR_RANGE_MAX_GT_MIN;
        }
        if (!field.unit?.trim()) {
          fErr.rangeUnit = TASK_FORM_LABELS.ERROR_FIELD_TYPE_REQUIRED;
        }
      }

      if (field.type === 'attachment') {
        if (!field.acceptedFileTypes?.length) {
          fErr.attachmentFileTypes = TASK_FORM_LABELS.ERROR_FIELD_TYPE_REQUIRED;
        }
        if (field.maxFileSizeMb === null || field.maxFileSizeMb === undefined) {
          fErr.attachmentMaxSize = TASK_FORM_LABELS.ERROR_FIELD_TYPE_REQUIRED;
        }
        if (field.maxFiles === null || field.maxFiles === undefined) {
          fErr.attachmentMaxFiles = TASK_FORM_LABELS.ERROR_FIELD_TYPE_REQUIRED;
        }
      }

      const hasOptions =
        field.type === 'dropdown' ||
        field.type === 'radio' ||
        field.type === 'checkbox';

      if (hasOptions) {
        const opts = field.options ?? [];
        if (opts.length === 0) {
          fErr.minOptions = TASK_FORM_LABELS.ERROR_FIELD_MIN_OPTIONS;
        } else {
          const optionErrors: Record<string, OptionErrors> = {};
          for (const opt of opts) {
            if (!opt.label.trim()) {
              optionErrors[opt.id] = {
                label: TASK_FORM_LABELS.ERROR_OPTION_LABEL_REQUIRED,
              };
            }
          }
          if (Object.keys(optionErrors).length > 0) {
            fErr.options = optionErrors;
          }
        }
      }

      if (Object.keys(fErr).length > 0) {
        fieldErrors[field.id] = fErr;
      }
    }

    if (Object.keys(fieldErrors).length > 0) {
      secErr.fields = fieldErrors;
    }

    if (Object.keys(secErr).length > 0) {
      sectionErrors[section.id] = secErr;
    }
  }

  if (Object.keys(sectionErrors).length > 0) {
    errors.sections = sectionErrors;
  }

  return errors;
}

export function hasErrors(errors: FormErrors): boolean {
  return (
    !!errors.name ||
    !!errors.description ||
    !!errors.general ||
    (!!errors.sections && Object.keys(errors.sections).length > 0)
  );
}

// ─── Granular error clearing ──────────────────────────────────────────────────

export function clearResolvedSectionErrors(
  sectionErr: SectionErrors,
  updated: FormSection
): SectionErrors {
  const result: SectionErrors = {};

  if (sectionErr.name && !updated.name.trim()) {
    result.name = sectionErr.name;
  }
  if (sectionErr.minFields && updated.fields.length === 0) {
    result.minFields = sectionErr.minFields;
  }

  if (sectionErr.fields) {
    const newFields: NonNullable<SectionErrors['fields']> = {};

    for (const [fieldId, fErr] of Object.entries(sectionErr.fields)) {
      const field = updated.fields.find((f) => f.id === fieldId);
      if (!field) {
        continue;
      }

      const newFErr: FieldErrors = {};
      if (fErr.label && !field.label.trim()) {
        newFErr.label = fErr.label;
      }
      if (fErr.type && !field.type) {
        newFErr.type = fErr.type;
      }
      if (fErr.rangeMin && field.min == null) {
        newFErr.rangeMin = fErr.rangeMin;
      }
      if (
        fErr.rangeMax &&
        (field.max == null || (field.min != null && field.max <= field.min))
      ) {
        newFErr.rangeMax = fErr.rangeMax;
      }
      if (fErr.rangeUnit && !field.unit?.trim()) {
        newFErr.rangeUnit = fErr.rangeUnit;
      }
      if (fErr.attachmentFileTypes && !field.acceptedFileTypes?.length) {
        newFErr.attachmentFileTypes = fErr.attachmentFileTypes;
      }
      if (fErr.attachmentMaxSize && field.maxFileSizeMb == null) {
        newFErr.attachmentMaxSize = fErr.attachmentMaxSize;
      }
      if (fErr.attachmentMaxFiles && field.maxFiles == null) {
        newFErr.attachmentMaxFiles = fErr.attachmentMaxFiles;
      }
      if (fErr.minOptions && !field.options?.length) {
        newFErr.minOptions = fErr.minOptions;
      }

      if (fErr.options) {
        const newOpts: NonNullable<FieldErrors['options']> = {};
        for (const [optId, optErr] of Object.entries(fErr.options)) {
          const opt = field.options?.find((o) => o.id === optId);
          if (opt && optErr.label && !opt.label.trim()) {
            newOpts[optId] = { label: optErr.label };
          }
        }
        if (Object.keys(newOpts).length > 0) {
          newFErr.options = newOpts;
        }
      }

      if (Object.keys(newFErr).length > 0) {
        newFields[fieldId] = newFErr;
      }
    }

    if (Object.keys(newFields).length > 0) {
      result.fields = newFields;
    }
  }

  return result;
}

// ─── Initial state factory ────────────────────────────────────────────────────

export function makeEmptyField(): FormField {
  return {
    id: crypto.randomUUID(),
    type: '',
    label: '',
    required: true,
  };
}

export function makeEmptySection(): FormSection {
  return {
    id: crypto.randomUUID(),
    name: '',
    description: '',
    fields: [],
  };
}

const INITIAL_STATE: FormBuilderState = {
  name: '',
  description: '',
  sections: [{ ...makeEmptySection(), fields: [makeEmptyField()] }],
  signatureRequired: true,
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useCreateForm() {
  const navigate = useNavigate();

  const [formState, setFormState] = useState<FormBuilderState>(INITIAL_STATE);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isErrorSnackbarOpen, setIsErrorSnackbarOpen] = useState(false);
  const [snackbarError, setSnackbarError] = useState('');
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [validationAttemptCount, setValidationAttemptCount] = useState(0);

  const isDirty =
    formState.name.trim() !== '' ||
    formState.description.trim() !== '' ||
    formState.sections.length > 1 ||
    formState.sections.some(
      (s) =>
        s.name.trim() !== '' ||
        s.description.trim() !== '' ||
        s.fields.length > 1 ||
        s.fields.some((f) => f.label.trim() !== '' || f.type !== '')
    );

  // ─── State mutators ────────────────────────────────────────────────────────

  const setName = useCallback(
    (name: string) => {
      setFormState((prev) => ({ ...prev, name }));
      if (errors.name) {
        setErrors((prev) => {
          const { name: _, ...rest } = prev;
          return rest;
        });
      }
    },
    [errors.name]
  );

  const setDescription = useCallback(
    (description: string) => {
      setFormState((prev) => ({ ...prev, description }));
      if (errors.description && description.trim()) {
        setErrors((prev) => {
          const { description: _, ...rest } = prev;
          return rest;
        });
      }
    },
    [errors.description]
  );

  const setSignatureRequired = useCallback((v: boolean) => {
    setFormState((prev) => ({ ...prev, signatureRequired: v }));
  }, []);

  const addSection = useCallback(() => {
    setFormState((prev) => ({
      ...prev,
      sections: [
        ...prev.sections,
        { ...makeEmptySection(), fields: [makeEmptyField()] },
      ],
    }));
  }, []);

  const updateSection = useCallback((idx: number, updated: FormSection) => {
    setFormState((prev) => ({
      ...prev,
      sections: prev.sections.map((s, i) => (i === idx ? updated : s)),
    }));
    setErrors((prev) => {
      const sectionErr = prev.sections?.[updated.id];
      if (!sectionErr) {
        return prev;
      }
      const newSectionErr = clearResolvedSectionErrors(sectionErr, updated);
      return {
        ...prev,
        sections: { ...prev.sections, [updated.id]: newSectionErr },
      };
    });
  }, []);

  const removeSection = useCallback((idx: number) => {
    setFormState((prev) => ({
      ...prev,
      sections: prev.sections.filter((_, i) => i !== idx),
    }));
  }, []);

  const reorderSections = useCallback((fromIdx: number, toIdx: number) => {
    setFormState((prev) => {
      const sections = [...prev.sections];
      const a = sections[fromIdx];
      const b = sections[toIdx];
      if (!a || !b) {
        return prev;
      }
      sections[fromIdx] = b;
      sections[toIdx] = a;
      return { ...prev, sections };
    });
  }, []);

  // ─── Submit ────────────────────────────────────────────────────────────────

  const executeSubmit = useCallback(async () => {
    const validationErrors = validateForm(formState);
    if (hasErrors(validationErrors)) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    const result = await createTaskForm(formState);
    setIsSubmitting(false);

    if (result.success) {
      navigate('/Formularze-zadań', {
        state: { navigationSuccessMessage: TASK_FORM_LABELS.CREATE_SUCCESS },
      });
    } else {
      setSnackbarError(result.error ?? TASK_FORM_LABELS.CREATE_SUCCESS);
      setIsErrorSnackbarOpen(true);
    }
  }, [formState, navigate]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      await executeSubmit();
    },
    [executeSubmit]
  );

  const handleSaveFromPreview = executeSubmit;

  // ─── Cancel ───────────────────────────────────────────────────────────────

  const handleCancel = useCallback(() => {
    if (isDirty) {
      setIsCancelModalOpen(true);
    } else {
      navigate('/Formularze-zadań');
    }
  }, [isDirty, navigate]);

  const handleCloseCancelModal = useCallback(() => {
    setIsCancelModalOpen(false);
  }, []);

  const handleConfirmCancel = useCallback(() => {
    setIsCancelModalOpen(false);
    navigate('/Formularze-zadań');
  }, [navigate]);

  // ─── Preview ──────────────────────────────────────────────────────────────

  const handleOpenPreview = useCallback(() => {
    const validationErrors = validateForPreview(formState);
    if (hasErrors(validationErrors)) {
      setErrors(validationErrors);
      setValidationAttemptCount((n) => n + 1);
      return;
    }
    setErrors({});
    setIsPreviewOpen(true);
  }, [formState]);
  const handleClosePreview = useCallback(() => setIsPreviewOpen(false), []);

  return {
    formState,
    errors,
    isSubmitting,
    isCancelModalOpen,
    isErrorSnackbarOpen,
    snackbarError,
    isPreviewOpen,
    validationAttemptCount,
    setName,
    setDescription,
    setSignatureRequired,
    addSection,
    updateSection,
    removeSection,
    reorderSections,
    handleSubmit,
    handleSaveFromPreview,
    handleCancel,
    handleCloseCancelModal,
    handleConfirmCancel,
    handleOpenPreview,
    handleClosePreview,
    handleCloseErrorSnackbar: () => setIsErrorSnackbarOpen(false),
  };
}

export type { FormErrors, SectionErrors, FieldErrors, OptionErrors, FieldType };
